import {gql} from "@apollo/client";

import {RUMAggregationType} from "@/pages/rum/v2/types.ts";

import {MonitoringServiceType, Service} from "../types/checks";

export interface RUMGraphData {
  CheckID: number;
  PageLoad?: number;
  Time: string;
}

enum RUMStatus {
  BETTER = "BETTER",
  WORSE = "WORSE",
  NEUTRAL = "NEUTRAL",
  NO_DATA = "NO_DATA",
}

export interface RUMServiceExtra {
  rowData: JSON;
  graphData?: RUMGraphData[];
  overallStatus: RUMStatus;
}

interface RUMServiceInfo extends RUMServiceExtra, Service {}

interface RUMTotalPerformance {
  sr?: number;
  et?: number;
}

export interface RUMListResponse {
  rumServices: {
    items: RUMServiceInfo[];
    perf: {
      dash: RUMTotalPerformance;
      graph: RUMTotalPerformance;
    };
  };
}

export const GQL_RUM_LIST = gql`
  query RUMList($limit: Int!, $offset: Int!) {
    rumServices(limit: $limit, offset: $offset) {
      items {
        id
        name
        msp_address
        supports_downtime_alerting
        is_paused
        cached_state_is_up
        rowData
        graphData {
          CheckID
          PageLoad
          Time
        }
        overallStatus
      }
      perf {
        dash {
          sr
          et
        }
        graph {
          sr
          et
        }
      }
    }
  }
`;

interface RUMHeaderPerformance {
  header: RUMTotalPerformance;
  br: RUMTotalPerformance;
}

export interface RUMHeaderResponse {
  rumHeader: {
    id: number;
    name: string;
    monitoring_service_type: MonitoringServiceType;
    msp_address: string;
    data: JSON;
    perf: RUMHeaderPerformance;
  };
}

export const GQL_RUM_HEADER = gql`
  query RUMHeader($id: Int!, $payload: RUMHeaderInput!) {
    rumHeader(id: $id, payload: $payload) {
      id
      name
      monitoring_service_type
      msp_address
      data
      perf {
        header {
          sr
          et
        }
        br {
          sr
          et
        }
      }
    }
  }
`;

interface RUMTabPerformance {
  trend_query: RUMTotalPerformance;
  hist_query: RUMTotalPerformance;
  breakdown_query: RUMTotalPerformance;
  donut_query: RUMTotalPerformance;
}

export interface RUMTabData {
  trend: JSON[];
  hist: JSON;
  breakdown: JSON;
  donut: JSON;
}

export interface RUMTabDataResponse {
  rumTabData?: {
    data: RUMTabData;
    perf: RUMTabPerformance;
  };
}

interface RUMSortInput {
  dir?: string;
  key?: string;
}

export interface RUMHeaderInput {
  agg: RUMAggregationType;
  start?: Date;
  end?: Date;
  url?: string;
  sort?: RUMSortInput;
  limit?: number;
  offset?: number;
}

export const GQL_RUM_TAB_DATA = gql`
  query RUMTabData($id: Int!, $tab: String!, $payload: RUMHeaderInput!) {
    rumTabData(id: $id, tab: $tab, payload: $payload) {
      data {
        trend
        hist
        breakdown
        donut
      }
      perf {
        trend_query {
          sr
          et
        }
        hist_query {
          sr
          et
        }
        breakdown_query {
          sr
          et
        }
        donut_query {
          sr
          et
        }
      }
    }
  }
`;

export interface RUMTableSegment extends JSON {
  URLGroup: string;
  URLGroupHash: string;
}

export interface RUMSegmentTableData {
  table?: RUMTableSegment[];
  table_total_rows: number;
  donut: JSON;
  trend: JSON;
  map: JSON;
  breakdown: JSON;
}

export interface RUMSegmentsDataResponse {
  rumPageLoadSegment: {
    data: RUMSegmentTableData;
    perf: {
      table: RUMTotalPerformance;
      trend: RUMTotalPerformance;
      map: RUMTotalPerformance;
      breakdown: RUMTotalPerformance;
    };
  };
}

export const GQL_RUM_SEGMENT_DATA = gql`
  query RUMPageLoadSegments($id: Int!, $tab: String!, $section: String!, $payload: RUMHeaderInput!) {
    rumPageLoadSegment(id: $id, tab: $tab, section: $section, payload: $payload) {
      data {
        table
        table_total_rows
        donut
        trend
        map
        breakdown
      }
      perf {
        table {
          sr
          et
        }
        trend {
          sr
          et
        }
        map {
          sr
          et
        }
        breakdown {
          sr
          et
        }
      }
    }
  }
`;

export interface RUMUrl {
  IsAjax: boolean;
  Title: string;
  URLGroup: string;
  URLGroupHash: string;
}

export interface RUMUrlsResponse {
  rumURLs: {
    data: RUMUrl[];
    perf: {
      urls: RUMTotalPerformance;
    };
  };
}

export const GQL_RUM_URLS = gql`
  query RUMUrls($id: Int!, $q: String!, $urlHash: String, $start: DateTime, $end: DateTime) {
    rumURLs(id: $id, q: $q, urlHash: $urlHash, start: $start, end: $end) {
      data {
        IsAjax
        Title
        URLGroup
        URLGroupHash
      }
    }
  }
`;
